import { ref } from 'vue'
import { toast } from 'vue-sonner'
import { useApi } from '../useApi.js'
import { usePostHog } from '../usePostHog.js'
import { useGlobalCall } from '../calls/useGlobalCall.js'
import { notifyTasksUpdated } from '../tasks/useTasksTodayCount.js'

/**
 * Composable pour l'envoi de PDF de devis par email
 * Limite: 60 lignes max
 */
export function useQuotePDFEmail(quoteId, quote, callbacks = {}) {
  const { updateStatus, loadQuote, loadHistory } = callbacks
  const sendingQuoteEmail = ref(false)
  const { post: apiPost } = useApi()
  const { captureException } = usePostHog()
  const { closeCallAfterQuoteSent } = useGlobalCall()

  const sendQuoteByEmail = async (payload = {}) => {
    const { ccEmails, intro, relance } = payload
    sendingQuoteEmail.value = true
    try {
      const response = await apiPost(`/api/quotes/${quoteId}/send-email`, {
        amount: quote.value?.quote_amount,
        ...(Array.isArray(ccEmails) && ccEmails.length ? { ccEmails } : {}),
        ...(intro ? { intro } : {}),
        ...(relance ? { relance } : {})
      }, {
        successMessage: 'Devis envoyé par email avec succès',
        showSuccessToast: true,
        errorMessage: 'Erreur lors de l\'envoi du devis par email'
      })
      
      // Le devis est parti : l'échange téléphonique s'achève ici. Placé AVANT
      // les rafraîchissements : si l'un d'eux échoue (statut refusé, réseau
      // coupé), le mail est déjà envoyé et l'appel doit se clore quand même.
      await closeCallAfterQuoteSent(quoteId)
      reportRelance(relance, response)

      if (updateStatus) {
        await updateStatus('devis_en_attente')
      }
      if (loadQuote) {
        await loadQuote()
      }
      if (loadHistory) {
        await loadHistory()
      }

      return true
    } catch (err) {
      // Capture error in PostHog with context
      captureException(err, {
        error_type: 'email_send_error',
        quote_id: quoteId,
        email_type: 'quote_pdf'
      })
      
      console.error(err)
      throw err
    } finally {
      sendingQuoteEmail.value = false
    }
  }

  const generateAndSendQuote = async () => {
    if (!quote.value?.quote_amount) {
      return false
    }
    
    try {
      await apiPost(`/api/quotes/${quoteId}/send-email`, {
        amount: quote.value.quote_amount
      }, {
        successMessage: 'Devis généré et envoyé par email avec succès',
        showSuccessToast: true,
        errorMessage: 'Erreur lors de l\'envoi du devis'
      })
      
      // Même règle que sendQuoteByEmail : le devis parti, l'échange se termine.
      await closeCallAfterQuoteSent(quoteId)

      await updateStatus('devis_en_attente')
      await loadQuote()
      await loadHistory()
      return true
    } catch (err) {
      // Capture error in PostHog with context
      captureException(err, {
        error_type: 'email_send_error',
        quote_id: quoteId,
        email_type: 'quote_pdf_generated_and_sent'
      })
      
      console.error(err)
      throw err
    }
  }

  return {
    sendingQuoteEmail,
    sendQuoteByEmail,
    generateAndSendQuote
  }
}

// Le devis est parti ; la relance, elle, a pu échouer : le dire sans masquer l'envoi.
function reportRelance(relance, response) {
  if (!relance) return
  if (response?.relancesProgrammees > 0) {
    notifyTasksUpdated()
    return
  }
  toast.warning('Le devis est parti, mais la relance n’a pas pu être programmée. Ajoutez-la depuis « Mes tâches du jour ».')
}
